import { z } from "zod"

import { documentSchema } from "./documents.ts"
import { API_ERROR_CODES } from "./errors.ts"
import { expenseSchema } from "./expenses.ts"
import { extractionSchema } from "./extractions.ts"
import {
  customerSyncSchema,
  invoiceSyncSchema,
  organisationBrandingSyncSchema,
  paymentReceiptSyncSchema,
  paymentSyncSchema,
  sellerProfileSyncSchema,
} from "./invoice-sync.ts"
import { logbookPeriodSchema } from "./logbook-periods.ts"
import { odometerReadingSchema } from "./odometer-readings.ts"
import { revisionSchema, syncEntitySchema } from "./sync.ts"
import { tripTrackSchema } from "./trip-tracks.ts"
import { tripSchema } from "./trips.ts"
import { vehicleSchema } from "./vehicles.ts"

/**
 * POST /api/v1/sync/push (T130, ADR-015): the offline outbox is pushed in batches. Every entry
 * is applied in its own transaction, so partial success is normal and the batch never rolls
 * back as a whole. The wire rules for client authors live in docs/specs/sync-push-api.md.
 */

/** A batch carries at most this many entries; more is answered with 413 PAYLOAD_TOO_LARGE. */
export const SYNC_PUSH_MAX_ENTRIES = 200
/** The JSON body of one push may be at most 1 MiB; more is answered with 413 PAYLOAD_TOO_LARGE. */
export const SYNC_PUSH_MAX_BODY_BYTES = 1_048_576
/** Per-device limits over a fixed one-minute window; exceeding either is 429 RATE_LIMITED. */
export const SYNC_PUSH_RATE_LIMIT = {
  windowSeconds: 60,
  pushesPerWindow: 30,
  entriesPerWindow: 2000,
} as const

/**
 * Per-user push limits over the same window, across all of the user's devices and organisations
 * (T141): rotating `device_id` cannot escape it. About three devices pushing flat out.
 */
export const SYNC_PUSH_USER_RATE_LIMIT = {
  windowSeconds: 60,
  pushesPerWindow: 90,
  entriesPerWindow: 6000,
} as const

/**
 * Total push limit of one team organisation across all members (T1042 section 4.4), checked after
 * the user bucket and before the device bucket. A refusal is 429 RATE_LIMITED with
 * `details.scope = "organisation"`.
 */
export const SYNC_PUSH_ORG_RATE_LIMIT = {
  windowSeconds: 60,
  pushesPerWindow: 300,
  entriesPerWindow: 20_000,
} as const

/** Per-user limit on GET /api/v1/changes over a fixed one-minute window; more is 429 RATE_LIMITED. */
export const SYNC_PULL_RATE_LIMIT = {
  windowSeconds: 60,
  requestsPerWindow: 120,
} as const

export const SYNC_PUSH_OPERATIONS = ["create", "update", "delete"] as const
export const syncPushOperationSchema = z.enum(SYNC_PUSH_OPERATIONS)
export type SyncPushOperation = z.infer<typeof syncPushOperationSchema>

/**
 * Outcome of one entry. The only source of truth for statuses: clients, server and docs
 * import this list.
 * - applied: written (an auto-merge is also applied)
 * - replayed: this (device_id, local_seq) was already applied; nothing was written again
 * - conflict: the same fields changed on the server since base_revision; `document` is the
 *   server version and `conflict_fields` names the overlap (terminal: the entry is consumed)
 * - held: not stored because of the entitlement: quota (code QUOTA_EXCEEDED) or a feature the plan
 *   lacks (code UPGRADE_REQUIRED); may be resent later
 * - rejected: terminal error (code says why); a replay returns the same answer
 * - retry: transient (DEPENDENCY_MISSING, INTERNAL); resend with back-off
 * - skipped: not attempted because an earlier entry for the same entity was not applied
 */
export const SYNC_PUSH_STATUSES = [
  "applied",
  "replayed",
  "conflict",
  "held",
  "rejected",
  "retry",
  "skipped",
] as const
export const syncPushStatusSchema = z.enum(SYNC_PUSH_STATUSES)
export type SyncPushStatus = z.infer<typeof syncPushStatusSchema>

/** `code` on a result is one of the shared API error codes (errors.ts), never a new list. */
export const syncPushResultCodeSchema = z.enum(API_ERROR_CODES)

export const syncPushEntrySchema = z.object({
  /** Device-local monotonic sequence; with `device_id` it is the idempotency key. */
  local_seq: z.number().int().nonnegative(),
  entity: syncEntitySchema,
  /** The record id; for `trip_tracks` it is the trip id. */
  entity_id: z.uuid(),
  operation: syncPushOperationSchema,
  /** Revision the client last saw; required for update (create ignores it, delete wins). */
  base_revision: revisionSchema.nullable().optional(),
  /**
   * create: the entity's create request fields; update: the changed fields only; delete:
   * `{ deleted_at }` (optional, defaults to the server clock). Validated per entity by the
   * existing create/update request schemas.
   */
  fields: z.record(z.string(), z.unknown()),
  /** Device clock when the change was made; recorded for skew warnings, never used to order. */
  client_time: z.string().datetime({ offset: true }),
})

export type SyncPushEntry = z.infer<typeof syncPushEntrySchema>

export const syncPushRequestSchema = z
  .object({
    device_id: z.string().trim().min(1).max(128),
    app: z.object({
      platform: z.string().trim().min(1).max(32),
      version: z.string().trim().min(1).max(64),
    }),
    entries: z.array(syncPushEntrySchema).min(1).max(SYNC_PUSH_MAX_ENTRIES),
  })
  .superRefine((request, ctx) => {
    const seen = new Set<number>()
    request.entries.forEach((entry, index) => {
      if (seen.has(entry.local_seq)) {
        ctx.addIssue({
          code: "custom",
          message: "local_seq must be unique within a batch",
          path: ["entries", index, "local_seq"],
        })
      }
      seen.add(entry.local_seq)
    })
  })

export type SyncPushRequest = z.infer<typeof syncPushRequestSchema>

/** The entity document attached to a result (lenient stored schemas, one per entity). */
export const syncPushDocumentSchema = z.union([
  tripSchema,
  vehicleSchema,
  odometerReadingSchema,
  tripTrackSchema,
  documentSchema,
  extractionSchema,
  expenseSchema,
  logbookPeriodSchema,
  invoiceSyncSchema,
  paymentReceiptSyncSchema,
  paymentSyncSchema,
  customerSyncSchema,
  organisationBrandingSyncSchema,
  sellerProfileSyncSchema,
])

export type SyncPushDocument = z.infer<typeof syncPushDocumentSchema>

export const syncPushResultSchema = z.object({
  local_seq: z.number().int().nonnegative(),
  status: syncPushStatusSchema,
  code: syncPushResultCodeSchema.optional(),
  message: z.string().optional(),
  details: z.unknown().optional(),
  /** Current server version: applied, replayed and conflict carry it. */
  document: syncPushDocumentSchema.optional(),
  /** `document.revision`, repeated for clients that do not decode the document. */
  revision: revisionSchema.optional(),
  /** Fields changed by both sides since base_revision (conflict only). */
  conflict_fields: z.array(z.string()).optional(),
})

export type SyncPushResult = z.infer<typeof syncPushResultSchema>

export const syncPushResponseSchema = z.object({
  request_id: z.string().min(1),
  /** Results in ascending `local_seq` order, one per request entry. */
  results: z.array(syncPushResultSchema),
  /**
   * Head of the organisation change log after this push. Informational: pull with the cursor
   * from your last pull (the changes feed returns your own writes too).
   */
  cursor: z.number().int().nonnegative(),
})

export type SyncPushResponse = z.infer<typeof syncPushResponseSchema>

/**
 * Shape of a stored receipt result (sync_receipts.result_json): lenient on purpose (code is
 * any string, no document), because the server replays rows written by older versions.
 */
export const syncPushStoredResultSchema = syncPushResultSchema
  .omit({ document: true, code: true })
  .extend({ code: z.string().optional() })

export type SyncPushStoredResult = z.infer<typeof syncPushStoredResultSchema>
